<template>
  <v-dialog
    v-model="dialog"
    :max-width="isWideDialog ? '1180px' : '760px'"
    :content-class="isWideDialog ? 'excel-patch-dialog-shell' : ''"
    persistent
    scrollable
  >
    <v-card
      class="excel-patch-dialog"
      :class="{ 'upload-dialog-state': !isWideDialog }"
    >
      <v-card-title class="dialog-title">
        <div class="dialog-heading">
          <span class="title-icon">
            <v-icon color="white" size="21">mdi-file-document</v-icon>
          </span>
          <span>อัปเดตข้อมูลด้วย Excel (Excel Patch)</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon class="dialog-close-btn" @click="closeDialog">
          <v-icon size="27">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="dialog-body">
        <div class="process-selector">
          <div class="process-selector-label">เลือกโหมดการอัปเดตข้อมูล</div>
          <v-tabs
            v-model="processMode"
            class="process-tabs"
            background-color="transparent"
            slider-color="#0868D8"
            slider-size="2"
            height="34"
            @change="onProcessModeChange"
          >
            <v-tab href="#patch">
              <v-icon size="16">mdi-file-document-edit-outline</v-icon>
              <span>Patch ข้อมูล (Excel Patch)</span>
            </v-tab>
            <v-tab href="#sync">
              <v-icon size="16">mdi-sync</v-icon>
              <span>Sync Invoice จาก BI</span>
            </v-tab>
            <v-tab href="#delete" active-class="delete-tab">
              <v-icon size="16">mdi-delete-circle</v-icon>
              <span>ลบ Invoice</span>
            </v-tab>
          </v-tabs>
        </div>

        <v-tabs-items v-model="processMode" class="process-tab-items" touchless>
          <v-tab-item value="patch">
            <div v-if="sheets.length === 0" class="upload-step">
              <div
                class="upload-area"
                :class="{ 'drag-over': isDragging }"
                @click="triggerFileUpload"
                @dragover.prevent="onDragOver"
                @dragleave.prevent="onDragLeave"
                @drop.prevent="onFileDropped"
              >
                <input
                  ref="fileInput"
                  type="file"
                  class="d-none"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  @change="onFileSelected"
                />
                <v-icon class="upload-icon">mdi-file-upload</v-icon>
                <div class="upload-title">อัปโหลดไฟล์</div>
                <div class="upload-subtitle">
                  รองรับไฟล์ Excel .xlsx และ .xls
                </div>
              </div>
              <p v-if="fileError" class="error-text">{{ fileError }}</p>
            </div>

            <div v-else class="single-page">
              <div class="file-bar">
                <div class="file-summary">
                  <span class="excel-file-icon">
                    <v-icon color="white" size="28">mdi-file-excel</v-icon>
                  </span>
                  <span class="file-label">ไฟล์ที่เลือก:</span>
                  <span class="file-name">{{ fileName }}</span>
                  <span class="file-meta">{{ sheets.length }} sheets</span>
                </div>
                <div>
                  <v-btn
                    outlined
                    color="#1976D2"
                    class="choose-file-btn"
                    @click="resetUpload"
                  >
                    <v-icon left class="choose-file-icon"
                      >mdi-file-upload</v-icon
                    >
                    เลือกไฟล์ใหม่
                  </v-btn>
                </div>
              </div>

              <v-row class="workspace-row">
                <v-col cols="12" md="4">
                  <div class="control-panel">
                    <div class="section-heading">1. เลือกข้อมูล</div>
                    <label class="field-label">
                      <v-icon small>mdi-table</v-icon>
                      เลือกชีต
                    </label>
                    <v-select
                      v-model="activeSheetIndex"
                      :items="sheetSelectItems"
                      item-text="text"
                      item-value="value"
                      outlined
                      hide-details
                      prepend-inner-icon="mdi-table-large"
                      @change="loadActiveSheetMapping"
                    ></v-select>

                    <!-- ชีตต้องมีอย่างน้อย 2 columns -->
                    <v-alert
                      v-if="!invoiceColumn || !valueColumn"
                      class="date-validation-alert"
                      color="#FFF4E5"
                      text-color="#9A5B00"
                    >
                      <div class="mapping-message">
                        <v-icon color="#C77700"
                          >mdi-alert-circle-outline</v-icon
                        >
                        <span>
                          ชีตต้องมีอย่างน้อย 2 columns โดย column แรกเป็น
                          Invoice number และ column ที่สองเป็น Value
                        </span>
                      </div>
                    </v-alert>

                    <!-- ชื่อชีตไม่ตรงกับคอลัมน์ใดในระบบ -->
                    <v-alert
                      v-else-if="!sheetMatchedColumn"
                      class="sheet-unmatched-alert"
                      color="#FEF3F2"
                      text-color="#B42318"
                    >
                      <div class="mapping-message">
                        <v-icon color="#D92D20">mdi-table-alert</v-icon>
                        <span>
                          ไม่พบคอลัมน์ในระบบที่ตรงกับชื่อชีต
                          <strong>"{{ activeSheet.name }}"</strong><br />
                          กรุณาตรวจสอบและแก้ไขชื่อชีต (sheet name) ในไฟล์ Excel
                          ให้ตรงกับคอลัมน์ที่ต้องการอัปเดต
                          แล้วอัปโหลดไฟล์ใหม่อีกครั้ง
                        </span>
                      </div>
                    </v-alert>

                    <!-- ชื่อชีตจับคู่กับคอลัมน์ในระบบได้ -->
                    <template v-else>
                      <div class="mapping-info sheet-matched-info">
                        <v-icon color="#0D6EDE">mdi-table-check</v-icon>
                        <span>
                          ชีตนี้จะถูกอัปเดตไปที่
                          <strong>{{ targetFieldLabel }}</strong> ในระบบ
                        </span>
                      </div>
                      <div class="mapping-info">
                        <v-icon color="#0D6EDE">mdi-information</v-icon>
                        <span>
                          ระบบจะใช้
                          <strong>Invoice</strong> เป็นข้อมูลอ้างอิง<br />
                          ในการอัปเดตคอลัมน์
                          <strong>{{ targetFieldLabel }}</strong>
                        </span>
                      </div>
                      <v-alert
                        v-if="hasInvalidDateRows"
                        class="date-validation-alert"
                        color="#FFF4E5"
                        text-color="#9A5B00"
                      >
                        <div class="mapping-message">
                          <v-icon color="#C77700">mdi-calendar-alert</v-icon>
                          <span>
                            <strong>พบข้อมูลวันที่ไม่ถูกต้อง</strong>
                            {{ invalidDateRows.length }} รายการ<br />
                            แถว Excel: {{ invalidDateRowNumbers }}<br />
                            รองรับรูปแบบ YYYY-MM-DD, DD/MM/YYYY
                            และวันที่มาตรฐานจาก Excel
                          </span>
                        </div>
                      </v-alert>
                      <div class="mapping-info warning-make-sure">
                        <v-icon color="#B86B00"
                          >mdi-alert-circle-outline</v-icon
                        >
                        <span>
                          กรุณาตรวจสอบข้อมูลในการอัปเดตคอลัมน์
                          <strong>{{ targetFieldLabel }}</strong>
                          อย่างถี่ถ้วน
                          ข้อมูลเดิมจะถูกเขียนทับและไม่สามารถย้อนกลับได้
                        </span>
                      </div>
                    </template>
                  </div>
                </v-col>

                <v-col cols="12" md="8">
                  <div class="preview-panel">
                    <div class="preview-heading">
                      <div class="section-heading">
                        2. ตรวจสอบตัวอย่างข้อมูล
                      </div>
                      <span class="row-count"
                        >{{ activePreviewRows.length }} rows</span
                      >
                    </div>
                    <div class="preview-table-wrap">
                      <table class="preview-data-table">
                        <thead>
                          <tr>
                            <th
                              v-for="column in activeSheet.columns"
                              :key="column.key"
                            >
                              {{ column.label }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(row, rowIndex) in activePreviewRows"
                            :key="row.__rowNumber || rowIndex"
                          >
                            <td
                              v-for="column in activeSheet.columns"
                              :key="column.key"
                              :class="{
                                'invalid-date-cell': isInvalidDateCell(
                                  row,
                                  column
                                ),
                              }"
                            >
                              {{ displayCellValue(row, column) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-tab-item>

          <v-tab-item value="sync">
            <div class="sync-page">
              <div class="sync-card">
                <div class="sync-section-title">เลือกวิธีระบุ Invoice</div>
                <div class="sync-method-options">
                  <label
                    class="sync-method-option"
                    :class="{
                      'sync-method-option--active': syncInputMode === 'manual',
                    }"
                  >
                    <input
                      v-model="syncInputMode"
                      type="radio"
                      value="manual"
                    />
                    <span class="sync-radio-dot"></span>
                    <span>กรอกเลข Invoice เอง</span>
                  </label>
                  <label
                    class="sync-method-option"
                    :class="{
                      'sync-method-option--active': syncInputMode === 'excel',
                    }"
                  >
                    <input v-model="syncInputMode" type="radio" value="excel" />
                    <span class="sync-radio-dot"></span>
                    <span>อัปโหลดไฟล์ Excel</span>
                  </label>
                </div>

                <transition :name="syncInputTransitionName" mode="out-in">
                  <div
                    v-if="syncInputMode === 'manual'"
                    key="sync-manual"
                    class="sync-manual-panel"
                  >
                    <label class="sync-input-label">กรอกเลข Invoice</label>
                    <v-text-field
                      v-model="syncManualText"
                      class="sync-invoice-textarea"
                      outlined
                      maxlength="20"
                      counter="20"
                      hide-details="auto"
                      placeholder="กรอกเลข Invoice 1 รายการ"
                    ></v-text-field>
                    <div v-if="syncManualError" class="sync-validation-error">
                      <v-icon color="#D92D20" size="17">
                        mdi-alert-circle-outline
                      </v-icon>
                      <span>{{ syncManualError }}</span>
                    </div>
                    <div class="sync-input-help">
                      กรอกได้ครั้งละ 1 Invoice และต้องไม่เกิน 20 ตัวอักษร
                    </div>
                  </div>

                  <div v-else key="sync-excel" class="sync-upload-panel">
                    <input
                      ref="syncFileInput"
                      type="file"
                      class="d-none"
                      accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                      @change="onSyncFileSelected"
                    />
                    <div
                      v-if="!syncFileName"
                      class="sync-upload-area"
                      :class="{ 'sync-upload-area--dragging': syncIsDragging }"
                      @click="triggerSyncFileUpload"
                      @dragover.prevent="syncIsDragging = true"
                      @dragleave.prevent="syncIsDragging = false"
                      @drop.prevent="onSyncFileDropped"
                    >
                      <v-icon color="#8FB6D9" size="52">
                        mdi-cloud-upload-outline
                      </v-icon>
                      <div class="sync-upload-title">
                        คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวาง
                      </div>
                      <div class="sync-upload-subtitle">
                        รองรับไฟล์ Excel .xlsx และ .xls โดยใช้คอลัมน์แรกเป็น
                        Invoice และเลือกชีตที่จะ Sync ได้ (สูงสุด
                        {{ syncSheetInvoiceLimit }} รายการต่อชีต)
                      </div>
                    </div>
                    <div v-else class="sync-file-summary">
                      <div class="sync-file-details">
                        <v-icon color="#159455" size="22"
                          >mdi-file-excel</v-icon
                        >
                        <span>{{ syncFileName }}</span>
                        <strong>{{ syncFileInvoices.length }} รายการ</strong>
                      </div>
                      <v-btn
                        outlined
                        small
                        color="#1976D2"
                        class="sync-reupload-btn"
                        @click="triggerSyncFileUpload"
                      >
                        <v-icon left small>mdi-file-upload-outline</v-icon>
                        อัปโหลดไฟล์ใหม่
                      </v-btn>
                    </div>
                    <p v-if="syncFileError" class="sync-error-text">
                      {{ syncFileError }}
                    </p>
                    <div
                      v-if="syncSheets.length > 1"
                      class="sync-sheet-selector"
                    >
                      <label class="sync-input-label">เลือกชีตที่จะ Sync</label>
                      <v-select
                        v-model="syncActiveSheetIndex"
                        :items="syncSheetSelectItems"
                        outlined
                        dense
                        hide-details
                        class="sync-sheet-select"
                      ></v-select>
                    </div>
                    <div
                      v-if="syncSheetOverLimit"
                      class="sync-validation-alert"
                    >
                      <v-icon color="#D92D20" size="20">
                        mdi-alert-circle-outline
                      </v-icon>
                      <span>
                        ชีตนี้มี Invoice {{ syncFileInvoices.length }} รายการ
                        ซึ่งเกิน {{ syncSheetInvoiceLimit }} รายการ<br />
                        แต่ละชีตต้องมี Invoice ไม่เกิน
                        {{ syncSheetInvoiceLimit }} รายการ
                        กรุณาเลือกชีตอื่นหรือแยกข้อมูลออกเป็นหลายชีต
                      </span>
                    </div>
                    <div
                      v-if="syncFileInvalidRows.length > 0"
                      class="sync-validation-alert"
                    >
                      <v-icon color="#D92D20" size="20">
                        mdi-alert-circle-outline
                      </v-icon>
                      <span>
                        พบ Invoice เกิน 20 ตัวอักษร
                        {{ syncFileInvalidRows.length }} รายการ<br />
                        แถว Excel: {{ syncInvalidRowNumbers }}
                      </span>
                    </div>
                  </div>
                </transition>

                <div
                  v-if="syncInvoiceList.length > 0"
                  class="sync-invoice-preview"
                >
                  <div class="sync-preview-heading">
                    <span>รายการ Invoice ที่จะ Sync</span>
                    <strong>{{ syncInvoiceList.length }} รายการ</strong>
                  </div>
                  <div class="sync-preview-list">
                    <div
                      v-for="(invoice, index) in syncInvoiceList"
                      :key="invoice + '-' + index"
                      class="sync-preview-item"
                      :class="{
                        'sync-preview-item--invalid': invoice.length > 20,
                      }"
                    >
                      <span>{{ invoice }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-tab-item>

          <v-tab-item value="delete">
            <div class="sync-page">
              <div class="sync-card delete-card">
                <div class="delete-warning-banner">
                  <v-icon color="#D92D20" size="20"
                    >mdi-alert-octagon-outline</v-icon
                  >
                  <span>
                    การลบ Invoice
                    เป็นการลบข้อมูลออกจากระบบอย่างถาวรและไม่สามารถย้อนกลับได้
                    กรุณาตรวจสอบรายการให้ถูกต้องก่อนดำเนินการ
                  </span>
                </div>

                <div class="sync-section-title">เลือกวิธีระบุ Invoice</div>
                <div class="sync-method-options">
                  <label
                    class="sync-method-option delete-method-option"
                    :class="{
                      'sync-method-option--active delete-method-option--active':
                        deleteInputMode === 'manual',
                    }"
                  >
                    <input
                      v-model="deleteInputMode"
                      type="radio"
                      value="manual"
                    />
                    <span class="sync-radio-dot"></span>
                    <span>กรอกเลข Invoice เอง</span>
                  </label>
                  <label
                    class="sync-method-option delete-method-option"
                    :class="{
                      'sync-method-option--active delete-method-option--active':
                        deleteInputMode === 'excel',
                    }"
                  >
                    <input
                      v-model="deleteInputMode"
                      type="radio"
                      value="excel"
                    />
                    <span class="sync-radio-dot"></span>
                    <span>อัปโหลดไฟล์ Excel</span>
                  </label>
                </div>

                <transition :name="deleteInputTransitionName" mode="out-in">
                  <div
                    v-if="deleteInputMode === 'manual'"
                    key="delete-manual"
                    class="sync-manual-panel"
                  >
                    <label class="sync-input-label">กรอกเลข Invoice</label>
                    <v-text-field
                      v-model="deleteManualText"
                      class="sync-invoice-textarea"
                      outlined
                      maxlength="20"
                      counter="20"
                      hide-details="auto"
                      placeholder="กรอกเลข Invoice 1 รายการ"
                    ></v-text-field>
                    <div v-if="deleteManualError" class="sync-validation-error">
                      <v-icon color="#D92D20" size="17">
                        mdi-alert-circle-outline
                      </v-icon>
                      <span>{{ deleteManualError }}</span>
                    </div>
                    <div class="sync-input-help">
                      กรอกได้ครั้งละ 1 Invoice และต้องไม่เกิน 20 ตัวอักษร
                    </div>
                  </div>

                  <div v-else key="delete-excel" class="sync-upload-panel">
                    <input
                      ref="deleteFileInput"
                      type="file"
                      class="d-none"
                      accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                      @change="onDeleteFileSelected"
                    />
                    <div
                      v-if="!deleteFileName"
                      class="sync-upload-area"
                      :class="{
                        'sync-upload-area--dragging': deleteIsDragging,
                      }"
                      @click="triggerDeleteFileUpload"
                      @dragover.prevent="deleteIsDragging = true"
                      @dragleave.prevent="deleteIsDragging = false"
                      @drop.prevent="onDeleteFileDropped"
                    >
                      <v-icon color="#8FB6D9" size="52">
                        mdi-cloud-upload-outline
                      </v-icon>
                      <div class="sync-upload-title">
                        คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาวาง
                      </div>
                      <div class="sync-upload-subtitle">
                        รองรับไฟล์ Excel .xlsx และ .xls โดยใช้คอลัมน์แรกเป็น
                        Invoice และเลือกชีตที่จะลบได้
                      </div>
                    </div>
                    <div v-else class="sync-file-summary">
                      <div class="sync-file-details">
                        <v-icon color="#159455" size="22"
                          >mdi-file-excel</v-icon
                        >
                        <span>{{ deleteFileName }}</span>
                        <strong>{{ deleteFileInvoices.length }} รายการ</strong>
                      </div>
                      <v-btn
                        outlined
                        small
                        color="#1976D2"
                        class="sync-reupload-btn"
                        @click="triggerDeleteFileUpload"
                      >
                        <v-icon left small>mdi-file-upload-outline</v-icon>
                        อัปโหลดไฟล์ใหม่
                      </v-btn>
                    </div>
                    <p v-if="deleteFileError" class="sync-error-text">
                      {{ deleteFileError }}
                    </p>
                    <div
                      v-if="deleteSheets.length > 1"
                      class="sync-sheet-selector"
                    >
                      <label class="sync-input-label">เลือกชีตที่จะลบ</label>
                      <v-select
                        v-model="deleteActiveSheetIndex"
                        :items="deleteSheetSelectItems"
                        outlined
                        dense
                        hide-details
                        class="sync-sheet-select"
                      ></v-select>
                    </div>
                    <div
                      v-if="deleteFileInvalidRows.length > 0"
                      class="sync-validation-alert"
                    >
                      <v-icon color="#D92D20" size="20">
                        mdi-alert-circle-outline
                      </v-icon>
                      <span>
                        พบ Invoice เกิน 20 ตัวอักษร
                        {{ deleteFileInvalidRows.length }} รายการ<br />
                        แถว Excel: {{ deleteInvalidRowNumbers }}
                      </span>
                    </div>
                  </div>
                </transition>

                <div
                  v-if="deleteInvoiceList.length > 0"
                  class="sync-invoice-preview delete-invoice-preview"
                >
                  <div class="sync-preview-heading">
                    <span>รายการ Invoice ที่จะลบ</span>
                    <strong>{{ deleteInvoiceList.length }} รายการ</strong>
                  </div>
                  <div class="sync-preview-list">
                    <div
                      v-for="(invoice, index) in deleteInvoiceList"
                      :key="invoice + '-' + index"
                      class="sync-preview-item"
                      :class="{
                        'sync-preview-item--invalid': invoice.length > 20,
                      }"
                    >
                      <span>{{ invoice }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>

      <v-card-actions
        v-if="
          processMode === 'sync' ||
          processMode === 'delete' ||
          sheets.length > 0
        "
        class="dialog-actions"
      >
        <v-btn
          outlined
          color="#90A4AE"
          class="cancel-main-btn"
          :disabled="isSubmitting"
          @click="closeDialog"
        >
          ยกเลิก
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="processMode === 'patch'"
          color="#0868E8"
          class="confirm-main-btn"
          :disabled="
            currentPayloadRows.length === 0 ||
            hasInvalidDateRows ||
            isSubmitting
          "
          :loading="isSubmitting"
          @click="confirmPatch"
        >
          ยืนยันอัปเดตข้อมูล
        </v-btn>
        <v-btn
          v-else-if="processMode === 'sync'"
          color="#0868E8"
          class="confirm-main-btn"
          :disabled="syncInvoiceList.length === 0 || hasInvalidSyncInvoices"
          @click="confirmSyncInvoices"
        >
          เริ่ม Sync Invoice
        </v-btn>
        <v-btn
          v-else
          color="#E03131"
          class="confirm-main-btn delete-main-btn"
          :disabled="deleteInvoiceList.length === 0 || hasInvalidDeleteInvoices"
          @click="confirmDeleteInvoices"
        >
          ลบ Invoice
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="confirmDialog" max-width="500px" persistent>
      <v-card class="patch-confirm-card">
        <v-overlay
          :value="isSubmitting"
          absolute
          color="#ffffff"
          opacity="0.9"
          class="patch-loading-overlay"
        >
          <div class="patch-loading-content">
            <v-progress-circular
              indeterminate
              color="#0868E8"
              size="52"
              width="4"
            ></v-progress-circular>
            <div class="patch-loading-text">กำลังอัปเดตข้อมูล...</div>
            <div class="patch-loading-subtext">
              กรุณารอสักครู่ อย่าปิดหน้าต่างนี้
            </div>
          </div>
        </v-overlay>
        <v-card-text class="patch-confirm-content">
          <div class="patch-confirm-icon">
            <v-icon color="#F59E0B" size="44">
              mdi-alert-circle-outline
            </v-icon>
          </div>
          <div class="patch-confirm-title">ยืนยันการอัปเดตข้อมูล?</div>
          <div class="patch-confirm-description">
            คุณกำลังอัปเดตคอลัมน์
            <strong>{{ targetFieldLabel }}</strong>
            จำนวน <strong>{{ currentPayloadRows.length }}</strong> รายการ
          </div>
          <div class="patch-confirm-warning">
            ข้อมูลเดิมจะถูกเขียนทับและไม่สามารถย้อนกลับได้
          </div>
        </v-card-text>
        <v-card-actions class="patch-confirm-actions">
          <v-btn
            outlined
            color="#90A4AE"
            class="patch-confirm-cancel"
            :disabled="isSubmitting"
            @click="confirmDialog = false"
          >
            ยกเลิก
          </v-btn>
          <v-btn
            color="#0868E8"
            class="patch-confirm-submit"
            :disabled="isSubmitting"
            :loading="isSubmitting"
            @click="submitPatch"
          >
            ยืนยันอัปเดต
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="syncConfirmDialog" max-width="500px" persistent>
      <v-card class="patch-confirm-card">
        <v-overlay
          :value="isSubmitting"
          absolute
          color="#ffffff"
          opacity="0.9"
          class="patch-loading-overlay"
        >
          <div class="patch-loading-content">
            <v-progress-circular
              indeterminate
              color="#0868E8"
              size="52"
              width="4"
            ></v-progress-circular>
            <div class="patch-loading-text">กำลัง Sync ข้อมูล...</div>
            <div class="patch-loading-subtext">
              กรุณารอสักครู่ อย่าปิดหน้าต่างนี้
            </div>
          </div>
        </v-overlay>
        <v-card-text class="patch-confirm-content">
          <div class="patch-confirm-icon sync-confirm-icon">
            <v-icon color="#0868E8" size="42">mdi-sync</v-icon>
          </div>
          <div class="patch-confirm-title">ยืนยันการ Sync Invoice?</div>
          <div class="patch-confirm-description">
            คุณกำลัง Sync ข้อมูล Invoice จาก BI จำนวน
            <strong>{{ syncInvoiceList.length }}</strong> รายการ
          </div>
          <div class="patch-confirm-warning sync-confirm-warning">
            กรุณาตรวจสอบเลข Invoice ให้ถูกต้องก่อนเริ่มดำเนินการ
          </div>
        </v-card-text>
        <v-card-actions class="patch-confirm-actions">
          <v-btn
            outlined
            color="#90A4AE"
            class="patch-confirm-cancel"
            :disabled="isSubmitting"
            @click="syncConfirmDialog = false"
          >
            ยกเลิก
          </v-btn>
          <v-btn
            color="#0868E8"
            class="patch-confirm-submit"
            :disabled="isSubmitting"
            :loading="isSubmitting"
            @click="submitSyncInvoices"
          >
            ยืนยัน Sync
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteConfirmDialog" max-width="500px" persistent>
      <v-card class="patch-confirm-card">
        <v-overlay
          :value="isSubmitting"
          absolute
          color="#ffffff"
          opacity="0.9"
          class="patch-loading-overlay"
        >
          <div class="patch-loading-content">
            <v-progress-circular
              indeterminate
              color="#E03131"
              size="52"
              width="4"
            ></v-progress-circular>
            <div class="patch-loading-text">กำลังลบ Invoice...</div>
            <div class="patch-loading-subtext">
              กรุณารอสักครู่ อย่าปิดหน้าต่างนี้
            </div>
          </div>
        </v-overlay>
        <v-card-text class="patch-confirm-content">
          <div class="patch-confirm-icon delete-confirm-icon">
            <v-icon color="#E03131" size="42">mdi-delete-alert-outline</v-icon>
          </div>
          <div class="patch-confirm-title">ยืนยันการลบ Invoice?</div>
          <div class="patch-confirm-description">
            คุณกำลังลบข้อมูล Invoice จำนวน
            <strong>{{ deleteInvoiceList.length }}</strong> รายการ
          </div>
          <div class="patch-confirm-warning delete-confirm-warning">
            ข้อมูลจะถูกลบออกจากระบบอย่างถาวรและไม่สามารถย้อนกลับได้
          </div>
        </v-card-text>
        <v-card-actions class="patch-confirm-actions">
          <v-btn
            outlined
            color="#90A4AE"
            class="patch-confirm-cancel"
            :disabled="isSubmitting"
            @click="deleteConfirmDialog = false"
          >
            ยกเลิก
          </v-btn>
          <v-btn
            color="#E03131"
            class="patch-confirm-submit"
            :disabled="isSubmitting"
            :loading="isSubmitting"
            @click="submitDeleteInvoices"
          >
            ยืนยันลบ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resultDialog" max-width="620px" persistent>
      <v-card class="patch-result-card">
        <v-card-text class="patch-result-content">
          <div
            class="patch-result-icon"
            :class="'patch-result-icon--' + resultType"
          >
            <v-icon :color="resultIconColor" size="44">
              {{ resultIcon }}
            </v-icon>
          </div>

          <div class="patch-result-heading">
            <div class="patch-result-title">{{ resultTitle }}</div>
          </div>

          <div class="patch-result-description">
            {{ resultMessage }}
          </div>

          <div v-if="failedInvoices.length > 0" class="failed-invoice-panel">
            <div class="failed-invoice-heading">
              <div>
                <div class="failed-invoice-title">
                  Invoice ที่อัปเดตไม่สำเร็จ
                </div>
                <div class="failed-invoice-count">{{ failedCount }} รายการ</div>
              </div>
              <div class="failed-invoice-actions">
                <v-btn
                  text
                  color="#B86B00"
                  class="failed-action-btn"
                  @click="copyFailedInvoices"
                >
                  <v-icon left small>
                    {{ failedCopied ? "mdi-check" : "mdi-content-copy" }}
                  </v-icon>
                  {{ failedCopied ? "คัดลอกแล้ว" : "คัดลอกทั้งหมด" }}
                </v-btn>
                <v-btn
                  outlined
                  color="#B86B00"
                  class="failed-action-btn"
                  @click="exportFailedInvoices"
                >
                  <v-icon left small>
                    {{ failedExported ? "mdi-check" : "mdi-download" }}
                  </v-icon>
                  {{ failedExported ? "Export แล้ว" : "Export Excel" }}
                </v-btn>
              </div>
            </div>
            <div class="failed-invoice-instruction">
              กรุณาคัดลอกหรือ Export รายการก่อนปิดหน้าต่าง
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="patch-result-actions">
          <v-btn
            :color="resultType === 'error' ? '#667085' : '#0868E8'"
            class="patch-result-close"
            :disabled="resultType === 'partial' && !failedActionCompleted"
            @click="closeResultDialog"
          >
            {{ resultType === "error" ? "กลับไปตรวจสอบ" : "ตกลง" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import * as XLSX from "xlsx";
import eventBus from "../event-bus";

const INVOICE_PATCH_COLUMNS = [
  { text: "Cus No.", value: "customer_no" },
  { text: "Cus Name.", value: "customer_name" },
  { text: "Customer Criteria", value: "customer_criteria" },
  { text: "ห้ามติดต่อ", value: "no_contact" },
  { text: "Factoring", value: "factoring" },
  { text: "Online Payment", value: "online_payment" },
  { text: "SoNumber", value: "so_number" },
  { text: "Invoice Date", value: "invoice_date" },
  { text: "วันเริ่มรอบใบแจ้งหนี้", value: "start_date" },
  { text: "วันสิ้นสุดรอบใบแจ้งหนี้", value: "end_date" },
  { text: "วันที่เริ่มสัญญา", value: "so_start_date" },
  { text: "วันที่สิ้นสุดสัญญา", value: "so_end_date" },
  { text: "ชื่อการเงิน", value: "finance_name" },
  { text: "Overdue", value: "over_due" },
  { text: "Amount (Inc. VAT)", value: "amount_after_vat" },
  { text: "Aging", value: "aging" },
  { text: "Plan Date", value: "plan_date" },
  { text: "วันที่วางบิล", value: "date_bill" },
  { text: "วิธีวางบิล", value: "billing_type_detail" },
  { text: "สถานะวางบิล", value: "billing_status" },
  { text: "หมายเหตุสถานะวางบิล", value: "billing_status_remark" },
  { text: "วางบิลพร้อมใบเสร็จ", value: "billing_checking" },
  { text: "หมายเหตุการวางบิล", value: "billing_remark" },
  { text: "สถานะเก็บเงิน", value: "status_payment" },
  { text: "กำหนดชำระ", value: "estimate_date" },
  { text: "วันที่ confirm", value: "confirm_date" },
  { text: "วันที่เงินเข้า", value: "incoming_paid_date" },
  { text: "ช่องทางการชำระเงิน", value: "payment_type" },
  { text: "Mainstatus", value: "paid" },
  { text: "Recheck", value: "recheck" },
  { text: "สถานะการชำระเงิน", value: "status_recheck" },
  { text: "ส่งตัดหนี้", value: "cut_debt" },
  { text: "ทวงหนี้", value: "asking_payment" },
  { text: "วันที่ดำเนินการ", value: "asking_payment_date" },
  { text: "ระยะเวลาคงเหลือ", value: "remain_day" },
  { text: "MEMO Date ครั้งที่ 1", value: "memo_date_one" },
  { text: "MEMO Date ครั้งที่ 2", value: "memo_date_two" },
  { text: "MEMO Date ครั้งที่ 3", value: "memo_date_three" },
  { text: "เลขที่เอกสารลดหนี้", value: "debt_reduction_docno" },
  { text: "สถานะออกใหม่", value: "reissue_status" },
  { text: "เลขที่ลดหนี้", value: "cn_number" },
  { text: "เลขที่ตัดหนี้", value: "rv_number" },
  { text: "เลขที่ใบเสร็จ", value: "receipt_number" },
  { text: "สถานะใบเสร็จ", value: "incoming_status" },
  { text: "หมายเหตุ Warroom", value: "remark_warroom" },
  { text: "User", value: "user_name" },
  { text: "วันที่แก้ไขล่าสุด", value: "remark_date" },
  { text: "สถานะ Inv.", value: "invoice_status" },
  { text: "PoNumber", value: "po_number" },
  { text: "Status Service", value: "status_service" },
  { text: "Service", value: "service" },
  { text: "Quotation No", value: "quotation" },
  { text: "Sales Representative", value: "sale_name" },
  { text: "Sale Team Lead", value: "sale_team" },
  { text: "Customer Description", value: "customer_description" },
  { text: "Email ลูกค้า", value: "customer_email" },
  { text: "Base Adjust", value: "base_adjust" },
  { text: "Aging Cash", value: "aging_cash" },
  { text: "SO Refer", value: "so_refer" },
  { text: "Email Sale", value: "sale_email" },
];

const DATE_TARGET_FIELDS = [
  "invoice_date",
  "start_date",
  "end_date",
  "so_start_date",
  "so_end_date",
  "plan_date",
  "date_bill",
  "estimate_date",
  "confirm_date",
  "incoming_paid_date",
  "asking_payment_date",
  "memo_date_one",
  "memo_date_two",
  "memo_date_three",
  "remark_date",
];

const SYNC_SHEET_INVOICE_LIMIT = 50;

export default {
  name: "ExcelPatchUploadDialog",
  data() {
    return {
      dialog: false,
      fileName: "",
      fileError: "",
      isDragging: false,
      patchFileReadToken: 0,
      sheets: [],
      processMode: "patch",
      syncInputMode: "manual",
      syncInputDirection: "next",
      syncManualText: "",
      syncFileName: "",
      syncSheets: [],
      syncActiveSheetIndex: null,
      syncFileError: "",
      syncIsDragging: false,
      syncFileReadToken: 0,
      deleteInputMode: "manual",
      deleteInputDirection: "next",
      deleteManualText: "",
      deleteFileName: "",
      deleteSheets: [],
      deleteActiveSheetIndex: null,
      deleteFileError: "",
      deleteIsDragging: false,
      deleteFileReadToken: 0,
      deleteConfirmDialog: false,
      activeSheetIndex: null,
      formMapping: {
        targetField: "",
      },
      confirmDialog: false,
      syncConfirmDialog: false,
      isSubmitting: false,
      resultDialog: false,
      resultType: "success",
      resultTitle: "",
      resultMessage: "",
      resultStatus: null,
      failedCount: 0,
      failedInvoices: [],
      failedCopied: false,
      failedExported: false,
      failedActionCompleted: false,
    };
  },
  computed: {
    isWideDialog() {
      return (
        this.processMode === "sync" ||
        this.processMode === "delete" ||
        this.sheets.length > 0
      );
    },
    syncManualInvoice() {
      return this.normalizePatchText(this.syncManualText);
    },
    syncManualError() {
      if (!this.syncManualInvoice) return "";
      if (/[,;\n\r\t]/.test(this.syncManualText)) {
        return "กรุณากรอก Invoice เพียง 1 รายการ";
      }
      if (this.syncManualInvoice.length > 20) {
        return "เลข Invoice ต้องไม่เกิน 20 ตัวอักษร";
      }
      return "";
    },
    syncInvoiceList() {
      return this.syncInputMode === "manual"
        ? this.syncManualInvoice
          ? [this.syncManualInvoice]
          : []
        : this.syncFileInvoices;
    },
    syncInputTransitionName() {
      return this.syncInputDirection === "next"
        ? "sync-slide-left"
        : "sync-slide-right";
    },
    syncSheetInvoiceLimit() {
      return SYNC_SHEET_INVOICE_LIMIT;
    },
    syncActiveSheet() {
      if (this.syncActiveSheetIndex === null) return null;
      return this.syncSheets[this.syncActiveSheetIndex] || null;
    },
    syncFileInvoices() {
      return this.syncActiveSheet ? this.syncActiveSheet.invoices : [];
    },
    syncFileInvalidRows() {
      return this.syncActiveSheet ? this.syncActiveSheet.invalidRows : [];
    },
    syncSheetSelectItems() {
      return this.syncSheets.map((sheet, index) => ({
        text: sheet.name + " (" + sheet.invoices.length + " รายการ)",
        value: index,
      }));
    },
    syncSheetOverLimit() {
      return this.syncFileInvoices.length > SYNC_SHEET_INVOICE_LIMIT;
    },
    hasInvalidSyncInvoices() {
      if (this.syncInputMode === "manual") {
        return Boolean(this.syncManualError);
      }
      return this.syncFileInvalidRows.length > 0 || this.syncSheetOverLimit;
    },
    syncInvalidRowNumbers() {
      const visibleRows = this.syncFileInvalidRows
        .slice(0, 8)
        .map((item) => item.rowNumber)
        .join(", ");
      const remainingCount = this.syncFileInvalidRows.length - 8;
      return remainingCount > 0
        ? visibleRows + " และอีก " + remainingCount + " แถว"
        : visibleRows;
    },
    deleteManualInvoice() {
      return this.normalizePatchText(this.deleteManualText);
    },
    deleteManualError() {
      if (!this.deleteManualInvoice) return "";
      if (/[,;\n\r\t]/.test(this.deleteManualText)) {
        return "กรุณากรอก Invoice เพียง 1 รายการ";
      }
      if (this.deleteManualInvoice.length > 20) {
        return "เลข Invoice ต้องไม่เกิน 20 ตัวอักษร";
      }
      return "";
    },
    deleteInvoiceList() {
      return this.deleteInputMode === "manual"
        ? this.deleteManualInvoice
          ? [this.deleteManualInvoice]
          : []
        : this.deleteFileInvoices;
    },
    deleteInputTransitionName() {
      return this.deleteInputDirection === "next"
        ? "sync-slide-left"
        : "sync-slide-right";
    },
    deleteActiveSheet() {
      if (this.deleteActiveSheetIndex === null) return null;
      return this.deleteSheets[this.deleteActiveSheetIndex] || null;
    },
    deleteFileInvoices() {
      return this.deleteActiveSheet ? this.deleteActiveSheet.invoices : [];
    },
    deleteFileInvalidRows() {
      return this.deleteActiveSheet ? this.deleteActiveSheet.invalidRows : [];
    },
    deleteSheetSelectItems() {
      return this.deleteSheets.map((sheet, index) => ({
        text: sheet.name + " (" + sheet.invoices.length + " รายการ)",
        value: index,
      }));
    },
    hasInvalidDeleteInvoices() {
      if (this.deleteInputMode === "manual") {
        return Boolean(this.deleteManualError);
      }
      return this.deleteFileInvalidRows.length > 0;
    },
    deleteInvalidRowNumbers() {
      const visibleRows = this.deleteFileInvalidRows
        .slice(0, 8)
        .map((item) => item.rowNumber)
        .join(", ");
      const remainingCount = this.deleteFileInvalidRows.length - 8;
      return remainingCount > 0
        ? visibleRows + " และอีก " + remainingCount + " แถว"
        : visibleRows;
    },
    activeSheet() {
      if (this.activeSheetIndex === null) return null;
      return this.sheets[this.activeSheetIndex] || null;
    },
    invoiceColumn() {
      if (!this.activeSheet || this.activeSheet.columns.length === 0)
        return null;
      return this.activeSheet.columns[0];
    },
    valueColumn() {
      if (!this.activeSheet || this.activeSheet.columns.length < 2) return null;
      return this.activeSheet.columns[1];
    },
    sheetMatchedColumn() {
      if (!this.activeSheet) return null;
      return this.matchColumnBySheetName(this.activeSheet.name);
    },
    targetFieldLabel() {
      const field = INVOICE_PATCH_COLUMNS.find(
        (item) => item.value === this.formMapping.targetField
      );
      return field ? field.text : "";
    },
    sheetSelectItems() {
      return this.sheets.map((sheet, index) => ({
        text:
          sheet.name +
          " (" +
          this.getPatchCandidateRows(sheet).length +
          " rows, " +
          sheet.columns.length +
          " columns)",
        value: index,
      }));
    },
    activePreviewRows() {
      if (!this.activeSheet) return [];
      return this.getPatchCandidateRows(this.activeSheet);
    },
    isDateTargetField() {
      return DATE_TARGET_FIELDS.includes(this.formMapping.targetField);
    },
    invalidDateRows() {
      if (
        !this.activeSheet ||
        !this.invoiceColumn ||
        !this.valueColumn ||
        !this.isDateTargetField
      ) {
        return [];
      }

      return this.getPatchCandidateRows(this.activeSheet).filter(
        (row) => !this.normalizeDateValue(row[this.valueColumn.key])
      );
    },
    hasInvalidDateRows() {
      return this.invalidDateRows.length > 0;
    },
    invalidDateRowNumbers() {
      const visibleRows = this.invalidDateRows
        .slice(0, 8)
        .map((row) => row.__rowNumber)
        .join(", ");
      const remainingCount = this.invalidDateRows.length - 8;
      return remainingCount > 0
        ? visibleRows + " และอีก " + remainingCount + " แถว"
        : visibleRows;
    },
    isCurrentMappingComplete() {
      return Boolean(
        this.invoiceColumn && this.valueColumn && this.formMapping.targetField
      );
    },
    currentPayload() {
      return {
        key: this.formMapping.targetField,
        data: this.currentPayloadRows,
      };
    },
    currentPayloadRows() {
      if (
        !this.activeSheet ||
        !this.isCurrentMappingComplete ||
        this.hasInvalidDateRows
      ) {
        return [];
      }
      return this.buildPayloadRowsFromMapping(
        this.activeSheet,
        this.formMapping
      );
    },
    resultIcon() {
      if (this.resultType === "error") return "mdi-close-circle-outline";
      if (this.resultType === "partial") return "mdi-alert-circle-outline";
      return "mdi-check-circle-outline";
    },
    resultIconColor() {
      if (this.resultType === "error") return "#D92D20";
      if (this.resultType === "partial") return "#C77700";
      return "#039855";
    },
  },
  watch: {
    syncInputMode(nextMode, previousMode) {
      const modeOrder = {
        manual: 0,
        excel: 1,
      };
      this.syncInputDirection =
        modeOrder[nextMode] >= modeOrder[previousMode] ? "next" : "previous";
      this.clearSyncInputData();
    },
    deleteInputMode(nextMode, previousMode) {
      const modeOrder = {
        manual: 0,
        excel: 1,
      };
      this.deleteInputDirection =
        modeOrder[nextMode] >= modeOrder[previousMode] ? "next" : "previous";
      this.clearDeleteInputData();
    },
  },
  mounted() {
    eventBus.$on("openExcelPatchUpload", this.openDialog);
  },
  beforeDestroy() {
    eventBus.$off("openExcelPatchUpload", this.openDialog);
  },
  methods: {
    openDialog() {
      this.clearResult();
      this.dialog = true;
    },
    closeDialog() {
      this.confirmDialog = false;
      this.syncConfirmDialog = false;
      this.deleteConfirmDialog = false;
      this.resultDialog = false;
      this.dialog = false;
      this.resetUpload();
      this.clearResult();
    },
    resetUpload() {
      this.processMode = "patch";
      this.resetPatchForm();
      this.resetSyncForm();
      this.resetDeleteForm();
    },
    resetPatchForm() {
      this.fileName = "";
      this.fileError = "";
      this.isDragging = false;
      this.patchFileReadToken += 1;
      this.sheets = [];
      this.activeSheetIndex = null;
      this.formMapping = this.emptyMapping();
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },
    onProcessModeChange() {
      this.confirmDialog = false;
      this.syncConfirmDialog = false;
      this.deleteConfirmDialog = false;
    },
    resetSyncForm() {
      this.syncInputMode = "manual";
      this.syncInputDirection = "next";
      this.clearSyncInputData();
    },
    clearSyncInputData() {
      this.syncManualText = "";
      this.syncFileName = "";
      this.syncSheets = [];
      this.syncActiveSheetIndex = null;
      this.syncFileError = "";
      this.syncIsDragging = false;
      this.syncFileReadToken += 1;
      if (this.$refs.syncFileInput) {
        this.$refs.syncFileInput.value = "";
      }
    },
    triggerSyncFileUpload() {
      if (this.$refs.syncFileInput) {
        this.$refs.syncFileInput.click();
      }
    },
    onSyncFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      this.readSyncExcelFile(file);
    },
    onSyncFileDropped(event) {
      this.syncIsDragging = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      this.readSyncExcelFile(file);
    },
    readSyncExcelFile(file) {
      this.syncFileError = "";
      this.syncFileName = "";
      this.syncSheets = [];
      this.syncActiveSheetIndex = null;
      if (!file) return;
      if (!this.isExcelFile(file)) {
        this.syncFileError = "กรุณาเลือกไฟล์ Excel นามสกุล .xlsx หรือ .xls";
        return;
      }

      const readToken = ++this.syncFileReadToken;
      const reader = new FileReader();
      reader.onload = (event) => {
        if (readToken !== this.syncFileReadToken) return;
        try {
          const workbook = XLSX.read(event.target.result, {
            type: "array",
            raw: false,
          });
          const sheets = workbook.SheetNames.map((sheetName) =>
            this.parseSyncSheet(sheetName, workbook.Sheets[sheetName])
          ).filter((sheet) => sheet.invoices.length > 0);

          if (sheets.length === 0) {
            this.syncFileError = "ไม่พบเลข Invoice ในคอลัมน์แรกของไฟล์ที่เลือก";
            return;
          }

          this.syncFileName = file.name;
          this.syncSheets = sheets;
          this.syncActiveSheetIndex = 0;
        } catch (error) {
          this.syncFileError =
            "อ่านไฟล์ Excel ไม่สำเร็จ กรุณาตรวจสอบไฟล์อีกครั้ง";
          console.error("Sync invoice Excel parse error:", error);
        }
      };
      reader.onerror = () => {
        if (readToken !== this.syncFileReadToken) return;
        this.syncFileError = "อ่านไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
      };
      reader.readAsArrayBuffer(file);
    },
    parseSyncSheet(sheetName, worksheet) {
      const matrix = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
        raw: false,
        blankrows: false,
      });
      const invoiceEntries = matrix
        .map((row, index) => ({
          invoice: this.normalizePatchText(row[0]),
          rowNumber: index + 1,
        }))
        .filter((item) => Boolean(item.invoice));
      const firstValue = String(
        (invoiceEntries[0] && invoiceEntries[0].invoice) || ""
      )
        .toLowerCase()
        .replace(/[\s._-]/g, "");
      if (
        firstValue === "invoice" ||
        firstValue === "invoiceno" ||
        firstValue === "invoicenumber"
      ) {
        invoiceEntries.shift();
      }

      return {
        name: sheetName,
        invoices: Array.from(
          new Set(invoiceEntries.map((item) => item.invoice))
        ),
        invalidRows: invoiceEntries.filter((item) => item.invoice.length > 20),
      };
    },
    confirmSyncInvoices() {
      if (this.syncInvoiceList.length === 0 || this.hasInvalidSyncInvoices) {
        return;
      }
      this.syncConfirmDialog = true;
    },
    async submitSyncInvoices() {
      if (this.syncInvoiceList.length === 0 || this.hasInvalidSyncInvoices) {
        return;
      }

      const bizId = localStorage.getItem("biz_id");
      if (!bizId) {
        this.syncConfirmDialog = false;
        this.showResult({
          type: "error",
          title: "ไม่พบรหัสบริษัท",
          message: "กรุณาเข้าสู่ระบบใหม่แล้วลองอีกครั้ง",
        });
        return;
      }

      const payload = {
        data: this.syncInvoiceList.map((invoiceNo) => ({
          invoice_no: String(invoiceNo).trim(),
        })),
      };

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "acces_token"
          )},${localStorage.getItem("secret")}`,
        },
      };

      this.isSubmitting = true;
      try {
        const response = await this.axios.post(
          process.env.VUE_APP_API +
            `/invoice/sync/bi?biz_id=${encodeURIComponent(bizId)}`,
          payload,
          config
        );

        this.syncConfirmDialog = false;
        const responseData = (response && response.data) || {};
        const notFoundInvoices = Array.isArray(responseData.notfound_invoices)
          ? responseData.notfound_invoices
          : [];
        const failedInvoices = Array.isArray(responseData.failed_invoices)
          ? responseData.failed_invoices
          : [];
        const unsyncedInvoices = [...notFoundInvoices, ...failedInvoices];

        if (unsyncedInvoices.length > 0) {
          this.showResult({
            type: "partial",
            title: "ข้อมูลถูกดึงเข้าสู่ระบบสำเร็จบางส่วน",
            message:
              "ระบบดึงข้อมูลสำเร็จบางส่วน บิลที่ไม่พบหรือดึงไม่สำเร็จจะถูกข้ามไป",
            status: 200,
            failedInvoices: unsyncedInvoices,
            failedCount: unsyncedInvoices.length,
          });
          return;
        }

        this.showResult({
          type: "success",
          title: "ดึงข้อมูลเรียบร้อยแล้ว",
          message: "ข้อมูลทั้งหมดถูกดึงเข้าสู่ระบบสำเร็จ",
          status: 200,
        });
      } catch (error) {
        const responseData = error.response && error.response.data;
        const errorMessage = this.getResponseMessage(
          responseData,
          "ไม่สามารถอัปเดตข้อมูลได้ กรุณาลองใหม่อีกครั้ง"
        );
        const responseStatus = Number(
          (responseData && responseData.status) ||
            (error.response && error.response.status)
        );
        this.syncConfirmDialog = false;
        this.showResult({
          type: "error",
          title: "อัปเดตข้อมูลไม่สำเร็จ",
          message: String(errorMessage),
          status: responseStatus || null,
        });
        console.error("Invoice sync error:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
    resetDeleteForm() {
      this.deleteInputMode = "manual";
      this.deleteInputDirection = "next";
      this.clearDeleteInputData();
    },
    clearDeleteInputData() {
      this.deleteManualText = "";
      this.deleteFileName = "";
      this.deleteSheets = [];
      this.deleteActiveSheetIndex = null;
      this.deleteFileError = "";
      this.deleteIsDragging = false;
      this.deleteFileReadToken += 1;
      if (this.$refs.deleteFileInput) {
        this.$refs.deleteFileInput.value = "";
      }
    },
    triggerDeleteFileUpload() {
      if (this.$refs.deleteFileInput) {
        this.$refs.deleteFileInput.click();
      }
    },
    onDeleteFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      this.readDeleteExcelFile(file);
    },
    onDeleteFileDropped(event) {
      this.deleteIsDragging = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      this.readDeleteExcelFile(file);
    },
    readDeleteExcelFile(file) {
      this.deleteFileError = "";
      this.deleteFileName = "";
      this.deleteSheets = [];
      this.deleteActiveSheetIndex = null;
      if (!file) return;
      if (!this.isExcelFile(file)) {
        this.deleteFileError = "กรุณาเลือกไฟล์ Excel นามสกุล .xlsx หรือ .xls";
        return;
      }

      const readToken = ++this.deleteFileReadToken;
      const reader = new FileReader();
      reader.onload = (event) => {
        if (readToken !== this.deleteFileReadToken) return;
        try {
          const workbook = XLSX.read(event.target.result, {
            type: "array",
            raw: false,
          });
          const sheets = workbook.SheetNames.map((sheetName) =>
            this.parseDeleteSheet(sheetName, workbook.Sheets[sheetName])
          ).filter((sheet) => sheet.invoices.length > 0);

          if (sheets.length === 0) {
            this.deleteFileError =
              "ไม่พบเลข Invoice ในคอลัมน์แรกของไฟล์ที่เลือก";
            return;
          }

          this.deleteFileName = file.name;
          this.deleteSheets = sheets;
          this.deleteActiveSheetIndex = 0;
        } catch (error) {
          this.deleteFileError =
            "อ่านไฟล์ Excel ไม่สำเร็จ กรุณาตรวจสอบไฟล์อีกครั้ง";
          console.error("Delete invoice Excel parse error:", error);
        }
      };
      reader.onerror = () => {
        if (readToken !== this.deleteFileReadToken) return;
        this.deleteFileError = "อ่านไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
      };
      reader.readAsArrayBuffer(file);
    },
    parseDeleteSheet(sheetName, worksheet) {
      const matrix = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
        raw: false,
        blankrows: false,
      });
      const invoiceEntries = matrix
        .map((row, index) => ({
          invoice: this.normalizePatchText(row[0]),
          rowNumber: index + 1,
        }))
        .filter((item) => Boolean(item.invoice));
      const firstValue = String(
        (invoiceEntries[0] && invoiceEntries[0].invoice) || ""
      )
        .toLowerCase()
        .replace(/[\s._-]/g, "");
      if (
        firstValue === "invoice" ||
        firstValue === "invoiceno" ||
        firstValue === "invoicenumber"
      ) {
        invoiceEntries.shift();
      }

      return {
        name: sheetName,
        invoices: Array.from(
          new Set(invoiceEntries.map((item) => item.invoice))
        ),
        invalidRows: invoiceEntries.filter((item) => item.invoice.length > 20),
      };
    },
    confirmDeleteInvoices() {
      if (
        this.deleteInvoiceList.length === 0 ||
        this.hasInvalidDeleteInvoices
      ) {
        return;
      }
      this.deleteConfirmDialog = true;
    },
    async submitDeleteInvoices() {
      if (
        this.isSubmitting ||
        this.deleteInvoiceList.length === 0 ||
        this.hasInvalidDeleteInvoices
      ) {
        return;
      }

      const bizId = localStorage.getItem("biz_id");
      if (!bizId) {
        this.deleteConfirmDialog = false;
        this.showResult({
          type: "error",
          title: "ไม่พบรหัสบริษัท",
          message: "กรุณาเข้าสู่ระบบใหม่แล้วลองอีกครั้ง",
        });
        return;
      }

      const payload = {
        data: this.deleteInvoiceList.map((invoiceNo) => ({
          invoice_no: String(invoiceNo).trim(),
        })),
      };

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "acces_token"
          )},${localStorage.getItem("secret")}`,
        },
        data: payload,
      };

      this.isSubmitting = true;
      try {
        const response = await this.axios.delete(
          process.env.VUE_APP_API +
            `/invoice/bi?biz_id=${encodeURIComponent(bizId)}`,
          config
        );

        this.deleteConfirmDialog = false;
        const responseData = (response && response.data) || {};
        const notFoundInvoices = Array.isArray(responseData.notfound_invoices)
          ? responseData.notfound_invoices
          : [];
        const failedInvoices = Array.isArray(responseData.failed_invoices)
          ? responseData.failed_invoices
          : [];
        const undeletedInvoices = [...notFoundInvoices, ...failedInvoices];

        if (undeletedInvoices.length > 0) {
          this.showResult({
            type: "partial",
            title: "ลบ Invoice สำเร็จบางส่วน",
            message:
              "ระบบลบข้อมูลสำเร็จบางส่วน บิลที่ไม่พบหรือลบไม่สำเร็จจะถูกข้ามไป",
            status: 200,
            failedInvoices: undeletedInvoices,
            failedCount: undeletedInvoices.length,
          });
          return;
        }

        this.showResult({
          type: "success",
          title: "ลบ Invoice เรียบร้อยแล้ว",
          message: "ข้อมูลทั้งหมดถูกลบออกจากระบบสำเร็จ",
          status: 200,
        });
      } catch (error) {
        const responseData = error.response && error.response.data;
        const errorMessage = this.getResponseMessage(
          responseData,
          "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง"
        );
        const responseStatus = Number(
          (responseData && responseData.status) ||
            (error.response && error.response.status)
        );
        this.deleteConfirmDialog = false;
        this.showResult({
          type: "error",
          title: "ลบ Invoice ไม่สำเร็จ",
          message: String(errorMessage),
          status: responseStatus || null,
        });
        console.error("Invoice delete error:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
    triggerFileUpload() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      this.readExcelFile(file);
    },
    onFileDropped(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      this.readExcelFile(file);
    },
    readExcelFile(file) {
      this.fileError = "";
      if (!file) return;
      if (!this.isExcelFile(file)) {
        this.fileError = "กรุณาเลือกไฟล์ Excel นามสกุล .xlsx หรือ .xls";
        return;
      }

      const readToken = ++this.patchFileReadToken;
      const reader = new FileReader();
      reader.onload = (event) => {
        if (readToken !== this.patchFileReadToken) return;
        try {
          const workbook = XLSX.read(event.target.result, {
            type: "array",
            cellDates: true,
            dateNF: "yyyy-mm-dd",
          });
          this.fileName = file.name;
          this.sheets = this.mapWorkbookSheets(workbook);
          if (this.sheets.length === 0) {
            this.fileError = "ไม่พบชีตที่อ่านข้อมูลได้ในไฟล์นี้";
            return;
          }
          this.activeSheetIndex = 0;
          this.loadActiveSheetMapping();
        } catch (error) {
          this.fileError = "อ่านไฟล์ Excel ไม่สำเร็จ กรุณาตรวจสอบไฟล์อีกครั้ง";
          console.error("Excel parse error:", error);
        }
      };
      reader.onerror = () => {
        if (readToken !== this.patchFileReadToken) return;
        this.fileError = "อ่านไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";
      };
      reader.readAsArrayBuffer(file);
    },
    isExcelFile(file) {
      const name = file.name ? file.name.toLowerCase() : "";
      return name.endsWith(".xlsx") || name.endsWith(".xls");
    },
    mapWorkbookSheets(workbook) {
      return workbook.SheetNames.map((sheetName, index) => {
        const worksheet = workbook.Sheets[sheetName];
        const matrix = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          raw: false,
          blankrows: false,
        });
        return this.createSheetModel(sheetName, matrix, index);
      }).filter((sheet) => sheet.columns.length > 0);
    },
    createSheetModel(sheetName, matrix, index) {
      const columnCount = this.getMaxColumnCount(matrix);
      const headerRow = matrix[0] || [];
      const columns = this.createColumns(headerRow, columnCount);
      const rows = matrix.slice(1).map((row, rowIndex) => {
        const item = { __rowNumber: rowIndex + 2 };
        columns.forEach((column, columnIndex) => {
          item[column.key] = this.normalizeCellValue(row[columnIndex]);
        });
        return item;
      });

      return {
        id: sheetName + "-" + index,
        name: sheetName,
        enabled: true,
        columns,
        rows,
        previewHeaders: columns.map((column) => ({
          text: column.label,
          value: column.key,
          sortable: false,
        })),
        mapping: this.emptyMapping(),
      };
    },
    getMaxColumnCount(matrix) {
      return matrix.reduce((max, row) => Math.max(max, row.length), 0);
    },
    createColumns(headerRow, columnCount) {
      const usedLabels = {};
      const columns = [];
      for (let index = 0; index < columnCount; index++) {
        const rawLabel = this.normalizeCellValue(headerRow[index]).trim();
        const label =
          rawLabel && !usedLabels[rawLabel]
            ? rawLabel
            : "Column " + this.columnName(index);
        usedLabels[label] = true;
        columns.push({
          key: label,
          label,
        });
      }
      return columns;
    },
    columnName(index) {
      let name = "";
      let current = index + 1;
      while (current > 0) {
        const remainder = (current - 1) % 26;
        name = String.fromCharCode(65 + remainder) + name;
        current = Math.floor((current - 1) / 26);
      }
      return name;
    },
    normalizeCellValue(value) {
      if (value === null || value === undefined) return "";
      if (value instanceof Date) {
        return this.formatDate(value);
      }
      return String(value)
        .replace(/\u00a0/g, " ")
        .trim();
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return year + "-" + month + "-" + day;
    },
    emptyMapping() {
      return {
        targetField: "",
      };
    },
    loadActiveSheetMapping() {
      if (!this.activeSheet) {
        this.formMapping = this.emptyMapping();
        return;
      }
      const matched = this.matchColumnBySheetName(this.activeSheet.name);
      this.formMapping = { targetField: matched ? matched.value : "" };
      this.activeSheet.mapping = Object.assign({}, this.formMapping);
    },
    matchColumnBySheetName(sheetName) {
      const normalized = this.normalizeMatchKey(sheetName);
      if (!normalized) return null;
      return (
        INVOICE_PATCH_COLUMNS.find(
          (column) =>
            this.normalizeMatchKey(column.value) === normalized ||
            this.normalizeMatchKey(column.text) === normalized
        ) || null
      );
    },
    normalizeMatchKey(value) {
      if (value === null || value === undefined) return "";
      return String(value).replace(/\s+/g, "").toLowerCase();
    },
    confirmPatch() {
      if (this.currentPayloadRows.length === 0 || this.hasInvalidDateRows)
        return;
      this.confirmDialog = true;
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (!copied) {
        throw new Error("Copy command was unsuccessful");
      }
    },
    async copyFailedInvoices() {
      if (this.failedInvoices.length === 0) return;

      try {
        await this.copyText(this.failedInvoices.join(", "));
        this.failedCopied = true;
        this.failedActionCompleted = true;
        setTimeout(() => {
          this.failedCopied = false;
        }, 1800);
      } catch (error) {
        console.error("Unable to copy failed invoices:", error);
      }
    },
    exportFailedInvoices() {
      if (this.failedInvoices.length === 0) return;

      try {
        const worksheet = XLSX.utils.aoa_to_sheet([
          ["invoice_no"],
          ...this.failedInvoices.map((invoice) => [invoice]),
        ]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Failed Invoices");
        XLSX.writeFile(
          workbook,
          "failed-invoices-" + this.formatDate(new Date()) + ".xlsx"
        );
        this.failedExported = true;
        this.failedActionCompleted = true;
      } catch (error) {
        console.error("Unable to export failed invoices:", error);
      }
    },
    showResult(options) {
      this.resultType = options.type || "error";
      this.resultTitle = options.title || "";
      this.resultMessage = options.message || "";
      this.resultStatus = options.status || null;
      this.failedInvoices = Array.isArray(options.failedInvoices)
        ? options.failedInvoices
        : [];
      this.failedCount =
        Number(options.failedCount) || this.failedInvoices.length;
      this.failedCopied = false;
      this.failedExported = false;
      this.failedActionCompleted = false;
      this.resultDialog = true;
    },
    clearResult() {
      this.resultType = "success";
      this.resultTitle = "";
      this.resultMessage = "";
      this.resultStatus = null;
      this.failedCount = 0;
      this.failedInvoices = [];
      this.failedCopied = false;
      this.failedExported = false;
      this.failedActionCompleted = false;
    },
    closeResultDialog() {
      this.resultDialog = false;
    },
    getResponseMessage(responseData, fallback) {
      return (
        (responseData &&
          (responseData.message ||
            responseData.detail ||
            responseData.error)) ||
        fallback
      );
    },
    async submitPatch() {
      if (
        this.isSubmitting ||
        this.currentPayloadRows.length === 0 ||
        this.hasInvalidDateRows
      ) {
        return;
      }

      const bizId = localStorage.getItem("biz_id");
      if (!bizId) {
        this.confirmDialog = false;
        this.showResult({
          type: "error",
          title: "ไม่พบรหัสบริษัท",
          message: "กรุณาเข้าสู่ระบบใหม่แล้วลองอีกครั้ง",
        });
        return;
      }

      const headers = {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Authorization:
            "Bearer " +
            localStorage.getItem("acces_token") +
            "," +
            localStorage.getItem("secret"),
        },
      };
      this.isSubmitting = true;
      try {
        const response = await this.axios.post(
          process.env.VUE_APP_API +
            "/invoice/pacthdata?biz_id=" +
            encodeURIComponent(bizId),
          this.currentPayload,
          headers
        );

        this.confirmDialog = false;
        const responseData = (response && response.data) || {};
        const responseStatus = Number(responseData.status || response.status);

        if (responseStatus >= 400) {
          this.showResult({
            type: "error",
            title: "อัปเดตข้อมูลไม่สำเร็จ",
            message: String(
              this.getResponseMessage(
                responseData,
                "ไม่สามารถอัปเดตข้อมูลได้ กรุณาลองใหม่อีกครั้ง"
              )
            ),
            status: responseStatus,
          });
          return;
        }

        const failedInvoices = Array.isArray(responseData.failed_invoices)
          ? responseData.failed_invoices
              .map((invoice) => String(invoice || "").trim())
              .filter(Boolean)
          : [];
        const failedCount =
          Number(responseData.failed_count) || failedInvoices.length;

        if (failedCount > 0 || failedInvoices.length > 0) {
          this.showResult({
            type: "partial",
            title: "อัปเดตข้อมูลสำเร็จบางส่วน",
            message:
              "ระบบอัปเดตข้อมูลแล้ว แต่มีบาง Invoice ที่ไม่สามารถอัปเดตได้ กรุณาคัดลอกรายการเพื่อตรวจสอบ",
            status: responseStatus || 200,
            failedCount,
            failedInvoices,
          });
          return;
        }

        this.showResult({
          type: "success",
          title: "อัปเดตข้อมูลเรียบร้อยแล้ว",
          message: "ข้อมูลทั้งหมดถูกอัปเดตสำเร็จ",
          status: responseStatus || 200,
        });
      } catch (error) {
        const responseData = error.response && error.response.data;
        const responseStatus = Number(
          (responseData && responseData.status) ||
            (error.response && error.response.status)
        );
        const errorMessage = this.getResponseMessage(
          responseData,
          "ไม่สามารถอัปเดตข้อมูลได้ กรุณาลองใหม่อีกครั้ง"
        );

        this.confirmDialog = false;
        this.showResult({
          type: "error",
          title: "อัปเดตข้อมูลไม่สำเร็จ",
          message: String(errorMessage),
          status: responseStatus || null,
        });
        console.error("Invoice patch error:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
    buildPayloadRowsFromMapping(sheet, mapping) {
      if (!mapping.targetField || !sheet || sheet.columns.length < 2) return [];
      return sheet.rows
        .map((row) => this.preparePayloadRow(row, sheet, mapping))
        .filter(Boolean);
    },
    getPatchCandidateRows(sheet) {
      if (!sheet || sheet.columns.length < 2) return [];
      const invoiceColumn = sheet.columns[0];
      const valueColumn = sheet.columns[1];

      return sheet.rows.filter(
        (row) =>
          this.hasPatchValue(row[invoiceColumn.key]) &&
          this.hasPatchValue(row[valueColumn.key])
      );
    },
    preparePayloadRow(row, sheet, mapping) {
      const invoiceColumn = sheet.columns[0];
      const valueColumn = sheet.columns[1];
      if (!invoiceColumn || !valueColumn) return null;

      const invoice = this.normalizePatchText(row[invoiceColumn.key]);
      const rawValue = row[valueColumn.key];
      if (!this.hasPatchValue(invoice) || !this.hasPatchValue(rawValue)) {
        return null;
      }

      const value = mapping.targetField
        ? this.normalizePayloadValue(rawValue, mapping.targetField)
        : this.normalizePatchText(rawValue);
      if (!this.hasPatchValue(value)) return null;

      return {
        invoice_no: invoice,
        value,
      };
    },
    hasPatchValue(value) {
      if (value === null || value === undefined) return false;
      const normalized = this.normalizePatchText(value);
      return normalized !== "" && normalized !== "-";
    },
    normalizePatchText(value) {
      if (value === null || value === undefined) return "";
      return String(value)
        .replace(/\u00a0/g, " ")
        .trim();
    },
    normalizePayloadValue(value, targetField) {
      if (!DATE_TARGET_FIELDS.includes(targetField)) {
        return this.normalizePatchText(value);
      }
      return this.normalizeDateValue(value);
    },
    normalizeDateValue(value) {
      if (value === null || value === undefined || value === "") return "";
      if (value instanceof Date) return this.formatDate(value);

      const text = this.normalizePatchText(value);
      if (!this.hasPatchValue(text)) return "";

      const compactDateMatch = text.match(/^(\d{4})(\d{2})(\d{2})$/);
      if (compactDateMatch) {
        return this.formatDateParts(
          compactDateMatch[1],
          compactDateMatch[2],
          compactDateMatch[3]
        );
      }

      if (/^\d+(\.\d+)?$/.test(text)) {
        const serialDate = Number(text);
        if (serialDate >= 20000 && serialDate <= 80000) {
          const parsedDate = XLSX.SSF.parse_date_code(serialDate);
          return parsedDate
            ? this.formatDateParts(parsedDate.y, parsedDate.m, parsedDate.d)
            : "";
        }
      }

      const isoMatch = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
      if (isoMatch) {
        return this.formatDateParts(isoMatch[1], isoMatch[2], isoMatch[3]);
      }

      const dayMonthYearMatch = text.match(
        /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})(?:\s.*)?$/
      );
      if (dayMonthYearMatch) {
        const first = Number(dayMonthYearMatch[1]);
        const second = Number(dayMonthYearMatch[2]);
        const day = second > 12 ? second : first;
        const month = second > 12 ? first : second;
        return this.formatDateParts(dayMonthYearMatch[3], month, day);
      }

      return "";
    },
    normalizeYear(year) {
      const normalized = Number(year);
      if (!Number.isFinite(normalized)) return null;
      if (String(year).length === 2) {
        return normalized >= 70 ? 1900 + normalized : 2000 + normalized;
      }
      return normalized > 2400 ? normalized - 543 : normalized;
    },
    formatDateParts(year, month, day) {
      const normalizedYear = this.normalizeYear(year);
      const normalizedMonth = Number(month);
      const normalizedDay = Number(day);
      if (
        !normalizedYear ||
        normalizedMonth < 1 ||
        normalizedMonth > 12 ||
        normalizedDay < 1 ||
        normalizedDay > 31
      ) {
        return "";
      }

      const date = new Date(
        Date.UTC(normalizedYear, normalizedMonth - 1, normalizedDay)
      );
      if (
        date.getUTCFullYear() !== normalizedYear ||
        date.getUTCMonth() + 1 !== normalizedMonth ||
        date.getUTCDate() !== normalizedDay
      ) {
        return "";
      }

      return [
        String(normalizedYear).padStart(4, "0"),
        String(normalizedMonth).padStart(2, "0"),
        String(normalizedDay).padStart(2, "0"),
      ].join("-");
    },
    displayCellValue(row, column) {
      if (
        this.valueColumn &&
        column.key === this.valueColumn.key &&
        this.isDateTargetField
      ) {
        return (
          this.normalizeDateValue(row[column.key]) ||
          this.normalizePatchText(row[column.key])
        );
      }
      return this.normalizePatchText(row[column.key]);
    },
    isInvalidDateCell(row, column) {
      if (
        !this.isDateTargetField ||
        !this.invoiceColumn ||
        !this.valueColumn ||
        column.key !== this.valueColumn.key
      ) {
        return false;
      }

      return (
        this.hasPatchValue(row[this.invoiceColumn.key]) &&
        this.hasPatchValue(row[column.key]) &&
        !this.normalizeDateValue(row[column.key])
      );
    },
  },
};
</script>

<style scoped>
::v-deep .excel-patch-dialog-shell {
  margin: 24px !important;
  max-height: 82vh !important;
}

.excel-patch-dialog {
  border: 1px solid #d7dfe8;
  border-radius: 10px !important;
  box-shadow: 0 10px 32px rgba(31, 45, 61, 0.16) !important;
  display: flex;
  font-family: "Bai_Jamjuree", sans-serif !important;
  height: auto;
  max-height: 86vh;
  overflow: hidden;
}

.excel-patch-dialog * {
  font-family: "Bai_Jamjuree", sans-serif;
}

.excel-patch-dialog.upload-dialog-state {
  height: auto;
  max-height: calc(100vh - 48px);
}

.dialog-title {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e1e7ee;
  color: #182230;
  flex: 0 0 68px;
  font-size: 20px !important;
  font-weight: 700;
  font-family: "Bai_Jamjuree", sans-serif !important;
  min-height: 68px;
  padding: 0 22px !important;
}

.dialog-heading {
  align-items: center;
  display: flex;
  line-height: 1.3;
}

.title-icon {
  align-items: center;
  background: linear-gradient(145deg, #63b7ff 0%, #3a87e8 100%);
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(34, 117, 219, 0.24);
  display: inline-flex;
  height: 34px;
  justify-content: center;
  margin-right: 17px;
  width: 34px;
}

.dialog-close-btn {
  color: #273442 !important;
  height: 42px !important;
  margin-right: -8px;
  width: 42px !important;
}

.dialog-body {
  background: #fff;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto !important;
  padding: 14px 22px 16px !important;
}

.dialog-actions {
  background: #fff;
  border-top: 1px solid #dce3eb;
  flex: 0 0 64px;
  min-height: 64px;
  padding: 9px 22px !important;
}

.dialog-actions ::v-deep .v-btn {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.cancel-main-btn {
  border-color: #c8d0d9 !important;
  border-radius: 6px;
  color: #4b5563 !important;
  height: 42px !important;
  min-width: 91px !important;
}

.confirm-main-btn {
  border-radius: 6px;
  box-shadow: 0 4px 10px rgba(8, 104, 232, 0.22);
  height: 42px !important;
  min-width: 188px !important;
  transition: color 0.2s ease;
}

.confirm-main-btn:not(.v-btn--disabled) {
  color: #fff !important;
}

.upload-step {
  margin: 0 auto;
  max-width: 500px;
}

.upload-area {
  align-items: center;
  border: 2px dashed #b8c7ce;
  border-radius: 6px;
  color: #616161;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 150px;
  text-align: center;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.upload-area.drag-over {
  background: #edf7fb;
  border-color: #3d8eb5;
}

.upload-icon {
  color: #aaa;
  font-size: 74px !important;
}

.upload-title {
  color: #666;
  font-size: 14px;
  font-weight: 400;
  margin-top: 2px;
}

.upload-subtitle {
  color: #999;
  font-size: 11px;
  margin-top: 2px;
}

.error-text {
  color: #f44336;
  font-size: 13px;
  margin-top: 12px;
  text-align: center;
}

.process-selector {
  margin-bottom: 14px;
}

.process-selector-label {
  color: #344050;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
}

.process-tabs {
  background: #e9eef4;
  border-radius: 8px;
  display: inline-block;
  padding: 4px;
  width: auto;
}

.process-tabs ::v-deep .delete-tab.v-tab--active {
  background: rgba(244, 0, 0, 0.75) !important;
  color: #fff !important;
}

.process-tabs ::v-deep .v-tabs-bar {
  background: transparent !important;
  height: 34px !important;
}

.process-tabs ::v-deep .v-slide-group__content {
  gap: 4px;
}

.process-tabs ::v-deep .v-tab {
  border-radius: 6px;
  color: #7b8794 !important;
  font-size: 13px;
  font-weight: 600;
  gap: 7px;
  letter-spacing: 0;
  min-width: 220px;
  padding: 0 16px;
  text-transform: none;
  transition: background-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
}

.process-tabs ::v-deep .v-tab .v-icon {
  color: inherit;
  margin-right: 7px;
}

.process-tabs ::v-deep .v-tab--active {
  background: #fff;
  box-shadow: 0 1px 4px rgba(35, 55, 80, 0.14);
  color: #0868d8 !important;
}

.process-tabs ::v-deep .v-tabs-slider-wrapper {
  display: none;
}

.process-tab-items {
  background: transparent !important;
  overflow: visible;
}

.sync-page {
  min-height: 430px;
}

.sync-card {
  background: #fff;
  border: 1px solid #dfe6ee;
  border-radius: 9px;
  min-height: 410px;
  padding: 18px 20px 20px;
}

.sync-section-title {
  color: #263442;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.sync-method-options {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
  margin-bottom: 18px;
}

.sync-method-option {
  align-items: center;
  color: #66717e;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
  position: relative;
}

.sync-method-option input {
  height: 0;
  opacity: 0;
  position: absolute;
  width: 0;
}

.sync-radio-dot {
  border: 2px solid #aeb8c3;
  border-radius: 50%;
  height: 15px;
  position: relative;
  width: 15px;
}

.sync-method-option--active {
  color: #0868d8;
}

.sync-method-option--active .sync-radio-dot {
  border-color: #0868d8;
}

.sync-method-option--active .sync-radio-dot::after {
  background: #0868d8;
  border-radius: 50%;
  content: "";
  height: 7px;
  left: 2px;
  position: absolute;
  top: 2px;
  width: 7px;
}

.sync-manual-panel,
.sync-upload-panel {
  margin-top: 4px;
}

.sync-slide-left-enter-active,
.sync-slide-left-leave-active,
.sync-slide-right-enter-active,
.sync-slide-right-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.sync-slide-left-enter,
.sync-slide-right-leave-to {
  opacity: 0;
  transform: translateX(28px);
}

.sync-slide-left-leave-to,
.sync-slide-right-enter {
  opacity: 0;
  transform: translateX(-28px);
}

.sync-input-label {
  color: #344050;
  display: block;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
}

.sync-invoice-textarea ::v-deep .v-input__slot {
  border-radius: 6px !important;
  min-height: 48px !important;
}

.sync-invoice-textarea ::v-deep input {
  font-size: 14px;
}

.sync-invoice-textarea ::v-deep .v-counter {
  color: #84909d;
  font-size: 11px;
}

.sync-validation-error {
  align-items: center;
  color: #d92d20;
  display: flex;
  font-size: 12px;
  gap: 6px;
  margin-top: 7px;
}

.sync-input-help {
  color: #84909d;
  font-size: 12px;
  margin-top: 7px;
}

.sync-upload-area {
  align-items: center;
  background: #fbfdff;
  border: 1px dashed #b9cfe3;
  border-radius: 7px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 150px;
  padding: 22px;
  text-align: center;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.sync-upload-area:hover,
.sync-upload-area--dragging {
  background: #f1f7fd;
  border-color: #4d98d6;
}

.sync-upload-title {
  color: #3b6f99;
  font-size: 14px;
  font-weight: 700;
  margin-top: 8px;
}

.sync-upload-subtitle {
  color: #8a98a6;
  font-size: 12px;
  margin-top: 5px;
}

.sync-file-summary {
  align-items: center;
  background: #f3fbf7;
  border: 1px solid #ccebd9;
  border-radius: 6px;
  color: #43515e;
  display: flex;
  font-size: 13px;
  gap: 8px;
  margin-top: 10px;
  padding: 9px 12px;
}

.sync-file-details {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 8px;
  min-width: 0;
}

.sync-file-details span {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sync-file-details strong {
  color: #087c43;
  flex: 0 0 auto;
}

.sync-reupload-btn {
  flex: 0 0 auto;
  font-size: 12px !important;
  font-weight: 700;
  letter-spacing: 0;
  margin-left: 14px;
  text-transform: none;
}

.sync-error-text {
  color: #d92d20;
  font-size: 13px;
  margin: 8px 0 0;
}

.sync-sheet-selector {
  margin-top: 14px;
}

.sync-sheet-selector .sync-input-label {
  margin-bottom: 6px;
}

.sync-sheet-select ::v-deep .v-input__slot {
  background: #f8fafc;
  border-radius: 8px;
  min-height: 44px;
}

.sync-sheet-select ::v-deep .v-select__selection {
  color: #1f2d3d;
  font-weight: 600;
}

.sync-validation-alert {
  align-items: flex-start;
  background: #fef3f2;
  border: 1px solid #fecdca;
  border-radius: 6px;
  color: #b42318;
  display: flex;
  font-size: 13px;
  gap: 8px;
  line-height: 1.55;
  margin-top: 10px;
  padding: 10px 12px;
}

.sync-invoice-preview {
  border: 1px solid #e0e7ee;
  border-radius: 6px;
  margin-top: 18px;
  overflow: hidden;
}

.sync-preview-heading {
  align-items: center;
  background: #f7f9fb;
  border-bottom: 1px solid #e0e7ee;
  color: #344050;
  display: flex;
  font-size: 13px;
  justify-content: space-between;
  padding: 10px 13px;
}

.sync-preview-heading strong {
  color: #0868d8;
}

.sync-preview-list {
  max-height: 180px;
  overflow-y: auto;
}

.sync-preview-item {
  color: #4b5967;
  font-family: Consolas, "Courier New", monospace;
  font-size: 13px;
  padding: 8px 13px;
}

.sync-preview-item + .sync-preview-item {
  border-top: 1px solid #edf1f5;
}

.sync-preview-item--invalid {
  background: #fef3f2;
  color: #b42318;
  font-weight: 700;
}

.delete-warning-banner {
  align-items: flex-start;
  background: #fef3f2;
  border: 1px solid #fecdca;
  border-radius: 7px;
  color: #b42318;
  display: flex;
  font-size: 13px;
  gap: 9px;
  line-height: 1.55;
  margin-bottom: 18px;
  padding: 11px 13px;
}

.delete-method-option--active {
  color: #d92d20;
}

.delete-method-option--active .sync-radio-dot {
  border-color: #d92d20;
}

.delete-method-option--active .sync-radio-dot::after {
  background: #d92d20;
}

.delete-invoice-preview .sync-preview-heading strong {
  color: #d92d20;
}

.delete-main-btn {
  box-shadow: 0 4px 10px rgba(224, 49, 49, 0.22);
}

.delete-main-btn:not(.v-btn--disabled) {
  color: #fff !important;
}

.file-bar {
  align-items: center;
  border: 1px solid #dfe6ee;
  border-radius: 9px;
  display: flex;
  gap: 16px;
  min-height: 62px;
  justify-content: space-between;
  margin-bottom: 14px;
  padding: 9px 12px 9px 14px;
}

.file-summary {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.excel-file-icon {
  align-items: center;
  background: linear-gradient(145deg, #14975a, #087c43);
  border-radius: 3px;
  box-shadow: 0 2px 5px rgba(8, 124, 67, 0.2);
  display: inline-flex;
  height: 39px;
  justify-content: center;
  margin-right: 10px;
  position: relative;
  width: 39px;
}

.excel-file-icon::after {
  background: #e7fff1;
  content: "";
  height: 30px;
  opacity: 0.9;
  position: absolute;
  right: -6px;
  top: 5px;
  width: 6px;
  z-index: 0;
}

.excel-file-icon ::v-deep .v-icon {
  position: relative;
  z-index: 1;
}

.file-label {
  color: #253040;
  font-size: 15px;
  font-weight: 600;
}

.file-name {
  color: #202b3a;
  font-size: 15px;
  font-weight: 700;
}

.file-meta {
  color: #687386;
  font-size: 14px;
  margin-left: 22px;
}

.choose-file-btn {
  border-color: #1b76ee !important;
  border-radius: 6px;
  height: 40px !important;
  min-width: 144px !important;
}

.choose-file-btn ::v-deep .v-btn__content {
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  gap: 7px;
  letter-spacing: 0;
  text-transform: none;
}

.choose-file-icon {
  font-size: 20px !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.patch-confirm-card {
  border-radius: 12px !important;
  font-family: "Bai_Jamjuree", sans-serif !important;
  overflow: hidden;
}

.patch-loading-overlay {
  border-radius: 12px;
}

.patch-loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  font-family: "Bai_Jamjuree", sans-serif !important;
}

.patch-loading-text {
  font-size: 16px;
  font-weight: 600;
  color: #0d2a4a;
}

.patch-loading-subtext {
  font-size: 13px;
  color: #5b6b7c;
  margin-top: -6px;
}

.patch-confirm-content {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 30px 34px 20px !important;
  text-align: center;
}

.patch-confirm-icon {
  align-items: center;
  background: #fff7e8;
  border: 1px solid #f6d596;
  border-radius: 50%;
  display: flex;
  height: 76px;
  justify-content: center;
  margin-bottom: 18px;
  width: 76px;
}

.sync-confirm-icon {
  background: #eef6ff;
  border-color: #b9d9ff;
}

.patch-confirm-title {
  color: #28313d;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.35;
}

.patch-confirm-description {
  color: #566170;
  font-size: 16px;
  line-height: 1.7;
  margin-top: 12px;
}

.patch-confirm-description strong {
  color: #1f2937;
}

.patch-confirm-warning {
  background: #fff7e8;
  border: 1px solid #f2cf91;
  border-radius: 7px;
  color: #8b5400;
  font-size: 14px;
  line-height: 1.5;
  margin-top: 16px;
  padding: 10px 14px;
  width: 100%;
}

.sync-confirm-warning {
  background: #eef6ff;
  border-color: #b9d9ff;
  color: #0b66d2;
}

.delete-confirm-icon {
  background: #fef3f2;
  border-color: #fecdca;
}

.delete-confirm-warning {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

.patch-confirm-actions {
  border-top: 1px solid #e4e9ef;
  gap: 10px;
  justify-content: center;
  padding: 14px 24px 18px !important;
}

.patch-confirm-actions ::v-deep .v-btn {
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  height: 42px !important;
  letter-spacing: 0;
  min-width: 128px !important;
  text-transform: none;
}

.patch-confirm-cancel {
  border-color: #c8d0d9 !important;
  color: #4b5563 !important;
}

.patch-confirm-submit {
  color: #fff !important;
}

.patch-result-card {
  border-radius: 12px !important;
  font-family: "Bai_Jamjuree", sans-serif !important;
  overflow: hidden;
}

.patch-result-content {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 30px 34px 22px !important;
  text-align: center;
}

.patch-result-icon {
  align-items: center;
  border: 1px solid;
  border-radius: 50%;
  display: flex;
  height: 76px;
  justify-content: center;
  margin-bottom: 18px;
  width: 76px;
}

.patch-result-icon--success {
  background: #ecfdf3;
  border-color: #abefc6;
}

.patch-result-icon--partial {
  background: #fff7e8;
  border-color: #f6d596;
}

.patch-result-icon--error {
  background: #fef3f2;
  border-color: #fecdca;
}

.patch-result-heading {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.patch-result-title {
  color: #28313d;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.35;
}

.patch-result-description {
  color: #566170;
  font-size: 16px;
  line-height: 1.65;
  margin-top: 12px;
  max-width: 500px;
  overflow-wrap: anywhere;
}

.failed-invoice-panel {
  background: #fffbf2;
  border: 1px solid #f2cf91;
  border-radius: 8px;
  margin-top: 20px;
  overflow: hidden;
  text-align: left;
  width: 100%;
}

.failed-invoice-heading {
  align-items: center;
  border-bottom: 1px solid #f2d9aa;
  display: flex;
  justify-content: space-between;
  padding: 12px 14px;
}

.failed-invoice-title {
  color: #704300;
  font-size: 15px;
  font-weight: 700;
}

.failed-invoice-count {
  color: #986000;
  font-size: 13px;
  margin-top: 2px;
}

.failed-invoice-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
}

.failed-action-btn {
  font-size: 13px !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.failed-invoice-instruction {
  color: #8b5400;
  font-size: 13px;
  line-height: 1.5;
  padding: 10px 14px;
  text-align: center;
}

.patch-result-actions {
  border-top: 1px solid #e4e9ef;
  justify-content: center;
  padding: 14px 24px 18px !important;
}

.patch-result-close {
  border-radius: 6px;
  color: #fff !important;
  font-size: 15px;
  font-weight: 600;
  height: 42px !important;
  letter-spacing: 0;
  min-width: 150px !important;
  text-transform: none;
}

.workspace-row {
  align-items: stretch;
  flex-wrap: nowrap;
  margin: 0 -7px !important;
}

.workspace-row > [class*="col-"] {
  display: flex;
  padding: 0 7px !important;
}

.workspace-row > .col-md-4 {
  flex: 0 0 32%;
  max-width: 32%;
}

.workspace-row > .col-md-8 {
  flex: 0 0 68%;
  max-width: 68%;
}

.control-panel,
.preview-panel {
  background: #fff;
  border: 1px solid #dfe6ee;
  border-radius: 9px;
  min-height: 470px;
  width: 100%;
}

.control-panel {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 20px 18px;
}

.preview-panel {
  padding: 16px;
}

.section-heading {
  color: #0769d7;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.4;
}

.control-panel > .section-heading {
  margin-bottom: 24px;
}

.field-label {
  align-items: center;
  color: #202b37;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 9px;
  margin-bottom: 12px;
}

.field-label ::v-deep .v-icon {
  color: #1f2933;
  font-size: 20px !important;
}

.patch-field-label {
  margin-top: 24px;
}

.patch-field-label ::v-deep .v-icon {
  font-size: 27px !important;
}

.preview-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0 7px 14px;
}

.row-count {
  background: #edf5ff;
  border-radius: 18px;
  color: #0d68d7;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 14px;
}

.mapping-alert {
  border: 1px solid #b9d9ff;
  border-radius: 8px;
  font-size: 14px;
  margin: 22px 0 0;
  padding: 13px !important;
}

.date-validation-alert {
  border: 1px solid #f2cf91;
  border-radius: 8px;
  font-size: 14px;
  margin: 12px 0 0;
  padding: 13px !important;
}

.mapping-message,
.mapping-info {
  align-items: flex-start;
  display: flex;
  gap: 13px;
  line-height: 1.65;
}

.mapping-info {
  background: #f3f8ff;
  border: 1px solid #b9d9ff;
  border-radius: 8px;
  color: #0b66d2;
  font-size: 14px;
  margin-top: 22px;
  min-height: 72px;
  padding: 13px;
}

.mapping-info.sheet-matched-info {
  align-items: center;
  min-height: 50px;
}

.mapping-info.warning-make-sure {
  background: #fff7e8;
  border: 1px solid #f2cf91;
  border-radius: 8px;
  color: #8b5400;
  font-size: 14px;
  margin-top: 22px;
  min-height: 72px;
  padding: 13px;
}

.mapping-info ::v-deep .v-icon,
.mapping-message ::v-deep .v-icon {
  flex: 0 0 auto;
  font-size: 21px !important;
  margin-top: 1px;
}

.sheet-unmatched-alert {
  margin-block: 10px;
}

.preview-table-wrap {
  border: 1px solid #d9e1e9;
  border-radius: 6px;
  height: 390px;
  overflow: auto;
  scrollbar-color: #aeb8c2 #edf1f4;
  scrollbar-width: thin;
}

.preview-data-table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
}

.preview-data-table th {
  background: linear-gradient(90deg, #0c568f 0%, #0064a6 100%) !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  height: 32px !important;
  line-height: 1.4 !important;
  padding: 5px 18px 4px;
  position: sticky;
  text-align: left;
  top: 0;
  vertical-align: middle !important;
  white-space: nowrap;
  z-index: 1;
  box-sizing: border-box;
}

.preview-data-table td {
  vertical-align: middle;
  border-bottom: 1px solid #dce3ea;
  color: #344050;
  font-size: 14px !important;
  height: 37px !important;
  overflow: hidden;
  padding: 0 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-data-table td.invalid-date-cell {
  background: #fff1f0 !important;
  color: #b42318;
  font-weight: 700;
}

.preview-data-table tbody tr:nth-child(even) {
  background: #fff;
}

.preview-data-table tbody tr:hover {
  background: #f4f8fc;
}

.control-panel ::v-deep .v-input {
  flex: 0 0 auto;
  font-size: 14px;
  margin: 0 !important;
}

.control-panel ::v-deep .v-input__slot {
  border-radius: 8px !important;
  margin-bottom: 0 !important;
  min-height: 50px !important;
  padding: 0 14px !important;
}

.control-panel ::v-deep .v-input__prepend-inner {
  align-self: center;
  margin: 0 12px 0 0 !important;
}

.control-panel ::v-deep .v-input__prepend-inner .v-icon {
  background: #eef5ff;
  border: 1px solid #cfe1fa;
  border-radius: 4px;
  color: #1670df;
  font-size: 20px;
  height: 28px;
  width: 28px;
}

.control-panel ::v-deep .v-select__selection {
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
  max-width: calc(100% - 4px);
}

.control-panel ::v-deep fieldset {
  border-color: #cfd7df;
}

.control-panel ::v-deep .v-label {
  font-size: 14px;
}

.control-panel ::v-deep .v-text-field__details {
  display: none;
}

@media (max-width: 960px) {
  ::v-deep .excel-patch-dialog-shell {
    margin: 16px !important;
    max-height: calc(100vh - 32px) !important;
  }

  .excel-patch-dialog {
    height: auto;
    max-height: calc(100vh - 32px);
  }

  .workspace-row {
    flex-wrap: wrap;
  }

  .workspace-row > .col-md-4,
  .workspace-row > .col-md-8 {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .workspace-row > .col-md-8 {
    padding-top: 14px !important;
  }

  .control-panel,
  .preview-panel {
    min-height: auto;
  }

  .preview-table-wrap {
    height: 420px;
  }
}

@media (max-width: 600px) {
  .dialog-title {
    flex-basis: 68px;
    font-size: 18px !important;
    min-height: 68px;
    padding: 0 16px !important;
  }

  .title-icon {
    margin-right: 10px;
  }

  .dialog-body {
    padding: 12px 14px 16px !important;
  }

  .process-tabs {
    display: block;
    width: 100%;
  }

  .process-tabs ::v-deep .v-slide-group__content {
    width: 100%;
  }

  .process-tabs ::v-deep .v-tab {
    flex: 1 1 50%;
    font-size: 11px;
    min-width: 0;
    padding: 0 8px;
  }

  .sync-card {
    padding: 16px 14px;
  }

  .sync-method-options {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .sync-upload-area {
    min-height: 135px;
    padding: 18px 12px;
  }

  .sync-file-summary {
    align-items: stretch;
    flex-direction: column;
  }

  .sync-reupload-btn {
    margin-left: 0;
    margin-top: 8px;
    width: 100%;
  }

  .file-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .file-bar .v-btn {
    width: 100%;
  }

  .file-meta {
    margin-left: 0;
  }

  .dialog-actions {
    padding: 12px 14px !important;
  }
}

.patch-column-autocomplete ::v-deep .v-input__slot {
  align-items: center !important;
  min-height: 50px !important;
}

.patch-column-autocomplete ::v-deep .v-select__slot {
  align-items: center !important;
  min-height: 50px !important;
}

.patch-column-autocomplete ::v-deep .v-select__selections {
  align-items: center !important;
  display: flex !important;
  flex-wrap: nowrap !important;
  min-height: 50px !important;
  overflow: hidden;
  padding: 0 !important;
}

.patch-column-autocomplete ::v-deep .v-select__selection {
  align-items: center !important;
  display: flex !important;
  margin: 0 !important;
  max-width: 100% !important;
  min-width: 0 !important;
  overflow: hidden;
}

.patch-selected-field {
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
}

.patch-selected-label {
  color: #1f2937;
  display: block;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.patch-selected-key {
  background: #eef5ff;
  border: 1px solid #cfe1fa;
  border-radius: 999px;
  color: #5d6877;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 4px 7px;
  white-space: nowrap;
}

.patch-column-autocomplete ::v-deep .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
}

.patch-column-autocomplete ::v-deep .v-input__prepend-inner {
  align-self: center !important;
  margin-top: 0 !important;
}
</style>
